import { useMemo, useState } from 'react';
import styled from 'styled-components';
import Menu, { MenuOption } from '../../Menu';
import { ModelGroup, ModelProvider } from '../types';
import { MenuPillButton, MenuPillChevron } from './menuPill';

const ModelButton = styled(MenuPillButton)`
  background: transparent;
  color: ${({ theme }) => theme.palette.common.gray12};

  &:hover {
    background: ${({ theme }) => theme.palette.common.gray9};
  }
`;

type Props = {
  groups: ModelGroup[];
  selectedModel: ModelProvider;
  onChange: (model: ModelProvider) => void;
};

const ModelMenuSelector = ({ groups, selectedModel, onChange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const allModels = useMemo(
    () => groups.flatMap((group) => group.models),
    [groups],
  );

  const options: MenuOption[] = useMemo(() => {
    const nonEmpty = groups.filter((group) => group.models.length > 0);
    const showHeaders = nonEmpty.length > 1;
    return nonEmpty.flatMap((group, index) => {
      const groupOptions = group.models.map((model) => ({
        value: model.modelName,
        label: model.label,
        description: model.description,
      }));
      if (showHeaders && group.title) {
        return [
          {
            value: `__group_header_${index}`,
            label: group.title,
            header: true,
          },
          ...groupOptions,
        ];
      }
      return groupOptions;
    });
  }, [groups]);

  if (allModels.length <= 1) {
    return null;
  }

  const handleChange = (modelName: string) => {
    const model = allModels.find((m) => m.modelName === modelName);
    if (model) {
      onChange(model);
    }
    setIsOpen(false);
  };

  return (
    <Menu
      menuButton={
        <ModelButton
          size="extraSmall"
          endIcon={<MenuPillChevron $isOpen={isOpen} />}
        >
          {selectedModel.label}
        </ModelButton>
      }
      options={options}
      optionsMaxHeight="400px"
      selectedOptions={[selectedModel.modelName]}
      highlightSelected
      onChange={(value) => handleChange(value as string)}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      closeOnChange
      anchorOrigin={{ vertical: -8, horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    />
  );
};

export default ModelMenuSelector;
